import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getCurrentUser } from '../../api/users';
import { registerPushNotifications, onForegroundPush } from '../../api/pushNotifications';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { token } = useAuth();
  const { addToast } = useToast();

  // Shared with Topbar's UserMenu (['current-user']) so this doesn't add an
  // extra network call — react-query dedupes by key.
  const { data: currentUser } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => getCurrentUser().then((r) => r.data),
    enabled: !!token,
  });

  // Live updates (notification badge, ride alerts, support messages) are
  // handled by each page/component polling its own query on an interval
  // instead of a shared WebSocket — see NotificationBell (30s), SafetyAlerts
  // (15s), Notifications page, and Support ticket thread/list.
  useEffect(() => {
    if (!token || !currentUser?.id) return undefined;
    registerPushNotifications();

    // FCM only auto-renders a system notification when the tab is
    // backgrounded (handled by firebase-messaging-sw.js); while focused we
    // have to surface it ourselves. Routine notifications get a plain toast,
    // but a safety alert (SOS / route deviation — see alert_type in
    // ride_alert_service.py's _notify_admins) is urgent enough that it
    // shouldn't depend on whether this tab happens to be focused right now,
    // so it also gets a real system notification and a red, longer-lived
    // toast instead of blending in as routine "info".
    let unsubscribeForeground = () => {};
    onForegroundPush((data) => {
      const isSafetyAlert = !!data.alert_type;
      if (isSafetyAlert && Notification.permission === 'granted') {
        const n = new Notification(data.title || 'Safety Alert', {
          body: data.body || '',
          icon: '/logo192.png',
          tag: `ride-alert-${data.ride_alert_id || ''}`,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      }
      addToast(
        data.body || data.title || 'New notification',
        isSafetyAlert ? 'error' : 'info',
        isSafetyAlert ? 10000 : 4000,
      );
    }).then((unsub) => {
      unsubscribeForeground = unsub;
    });

    return () => {
      unsubscribeForeground();
    };
  }, [token, currentUser?.id, addToast]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-theme">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Topbar onMenuClick={() => setSidebarOpen(true)} />

      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}
